<template>
  <div class="main-container">
    <SidebarNav @update:sidebarNavCollapsed="sidebarNavCollapsed = $event" />
    <SearchBar :sidebar-nav-collapsed="sidebarNavCollapsed" />
    <div
      class="settlements-container"
      :class="{['settlements-container-sidebar-collapse']: sidebarNavCollapsed}"
    >
      <div class="settlements-sub-container">
        <div class="settlements-page-header">
          <div class="settlements-page-header-title">
            {{ $t('deposits.title') }}
          </div>
        </div>

        <div class="table-header-description">
          {{ $t('deposits.description') }}
        </div>

        <div class="currency-tabs" v-if="showCurrencyTabs">
          <div
            class="currency-tab"
            v-for="currencyTab in currencyTabs"
            :key="currencyTab"
            :class="{'currency-tab-active': currencyTab === selectedCurrency}"
            :id="getElementId('currency-' + currencyTab)"
            @click="() => selectCurrency(currencyTab)"
          >
            {{ currencyTab === ALL_CURRENCIES ? $t('deposits.all_currencies') : currencyTab }}
          </div>
        </div>

        <div class="deposit-overview-data" :key="sidebarNavCollapsed + windowWidth">
          <div class="sub-data-container">
            <div class="section-header">
              {{ $t('deposits.overview') }}
            </div>
            <div class="sub-data">
              <div class="deposit-statistics-container total-payouts-card">
                <div class="deposit-data-container" v-if="!summaryLoading">
                  <div class="deposit-main-container">
                    <div class="deposit-sub-data-container">
                      <div class="title-container">
                        <div class="statistics-title">
                          {{ $t('deposits.total_payouts') }}
                        </div>
                        <div class="quarter-select">
                          <div
                            class="quarter-select-trigger"
                            :id="getElementId('quarter-select')"
                            @click="showQuarterDropdown = !showQuarterDropdown"
                          >
                            <span>{{ selectedQuarter.key }}</span>
                            <c-icon name="chevron-down" :size="12" />
                          </div>
                          <template v-if="showQuarterDropdown">
                            <div class="quarter-select-overlay" @click="showQuarterDropdown = false"></div>
                            <div class="quarter-select-menu">
                              <div
                                class="quarter-select-option"
                                v-for="quarter in quarterOptions"
                                :key="quarter.key"
                                :class="{'quarter-select-option-active': quarter.key === selectedQuarter.key}"
                                :id="getElementId('quarter-' + quarter.key)"
                                @click="() => selectQuarter(quarter.key)"
                              >
                                <span class="quarter-select-option-key">{{ quarter.key }}</span>
                                <span class="quarter-select-option-range">{{ quarter.range }}</span>
                              </div>
                            </div>
                          </template>
                        </div>
                      </div>
                      <div class="statistics-amount">
                        <template v-if="hasAmount(total)">
                          {{ formatter({type: 'currency', data: total, currencyCode: aggregateCurrency}) }}
                          <span class="currency-code-suffix" v-if="showCurrencyTabs">{{ aggregateCurrency }}</span>
                        </template>
                        <ToolTip
                          v-else
                          placement="top-start"
                          :content="amountUnavailableTooltip"
                          :arrow="false"
                        >
                          <span class="amount-unavailable">{{ $t('deposits.amount_unavailable') }}</span>
                        </ToolTip>
                      </div>
                      <div class="last-settled">
                        <span>{{ $t('deposits.last_settled') }}: </span>
                        <span v-if="lastSettledAt">
                          {{ formatter({type: 'zonedDate', data: lastSettledAt}) }}
                        </span>
                        <span v-else>{{ $t('deposits.never_settled') }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="statistics-footer">
                    {{ $t('deposits.deposit_q4_footer') }}
                  </div>
                </div>
                <div class="statistics-container" v-else>
                  <div class="data-container">
                    <div class="box loading-shimmer"></div>
                  </div>
                </div>
              </div>
              <div class="deposit-statistics-container reserve-card" v-if="getActivePayfac === 'ADYEN'">
                <div class="deposit-data-container" v-if="!fundsOnHoldDepositLoading">
                  <div class="deposit-main-container">
                    <div class="deposit-sub-data-container">
                      <div class="title-container">
                        <div class="funds-in-reserve">
                          <div class="statistics-title">
                            <span>{{ $t('deposits.funds_in_reserve') }}</span>
                          </div>
                          <ToolTip
                            placement="top-start"
                            :content="getChargebackReserveInfo + '<br>' + getRefundReserveInfo"
                            :no-wrap="true"
                            :arrow="false"
                          >
                            <img src="@/app/assets/images/info-outline.svg" alt="info icon" />
                          </ToolTip>
                        </div>
                        <div class="add-fund" v-if="!isAdminMode && isMerchantApproved " @click="navigateToAddFunds">
                          <c-icon name="plus" :size="12" />
                          <span>{{ $t('addFundsToReserve.actions.addFunds') }}</span>
                        </div>
                      </div>
                      <div class="statistics-amount">
                        {{
                          formatter({
                            type: 'currency',
                            data: fundsInReserve,
                            currencyCode: getMerchantCurrency
                          })
                        }}
                        <span class="currency-code-suffix" v-if="showCurrencyTabs">{{ getMerchantCurrency }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="statistics-footer">
                    {{ getFundsInReserveFooter }}
                  </div>
                </div>
                <div class="statistics-container" v-else>
                  <div class="data-container">
                    <div class="box loading-shimmer"></div>
                  </div>
                </div>
              </div>
              <div class="deposit-statistics-container reserve-card" v-else>
                <div class="deposit-data-container" v-if="!fundsOnHoldDepositLoading">
                  <div class="deposit-main-container">
                    <div class="deposit-sub-data-container">
                      <div class="title-container">
                        <div class="statistics-title">
                          {{ $t('deposits.funds_on_hold') }}
                        </div>
                      </div>
                      <div class="statistics-amount">
                        {{
                          formatter({
                            type: 'currency',
                            data: fundsOnHold,
                            currencyCode: getMerchantCurrency
                          })
                        }}
                        <span class="currency-code-suffix" v-if="showCurrencyTabs">{{ getMerchantCurrency }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="statistics-footer">
                    {{ $t('deposits.funds_on_hold_footer') }}
                  </div>
                </div>
                <div class="statistics-container" v-else>
                  <div class="data-container">
                    <div class="box loading-shimmer"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="sub-data-container">
            <div class="sub-data">
              <div class="deposit-statistics-container">
                <div class="deposit-data-container">
                  <div class="card-title">
                    {{ $t('deposits.estimated_future_deposits') }}
                  </div>
                  <template v-if="!fundsOnHoldDepositLoading">
                    <div class="nav">
                      <div
                        class="nav-item"
                        v-for="tab in tabs"
                        :key="tab.name"
                        :class="{'nav-item-active': tab.name === currentTab, [tab.name]: true}"
                        @click="() => switchTab(tab.name)"
                        :id="getElementId('tab-' + tab.name)"
                      >
                        <span>{{ $t('deposits.tabs.' + getEstimatedTabs(tab.name)) }}</span>
                      </div>
                    </div>
                    <div class="date-of-transactions-info-row" v-if="currentFutureDeposit.transactionTimeline.first_transaction_date">
                      <div>
                        {{ $t('deposits.date_of_transactions') }}
                      </div>
                      <div>
                        : {{ formatTransactionsTimelineForASettlement(currentFutureDeposit.transactionTimeline, 'zonedDate') }}
                      </div>
                    </div>
                    <div class="estimate-metrics">
                      <div class="estimate-metric">
                        <div class="estimate-metric-label">
                          {{ $t('deposits.total_amount') }}
                        </div>
                        <div class="estimate-metric-value">
                          <template v-if="hasAmount(currentFutureDeposit.totalAmount)">
                            {{
                              formatter({type: 'currency', data: currentFutureDeposit.totalAmount, currencyCode: aggregateCurrency})
                            }}
                          </template>
                          <span class="amount-unavailable" v-else>{{ $t('deposits.amount_unavailable') }}</span>
                        </div>
                      </div>
                      <div class="estimate-metric">
                        <div class="estimate-metric-label">
                          {{ $t('deposits.deductions') }}
                        </div>
                        <div class="estimate-metric-value">
                          <template v-if="hasAmount(currentFutureDeposit.totalFee)">
                            {{ formatter({type: 'currency', data: currentFutureDeposit.totalFee, currencyCode: aggregateCurrency}) }}
                          </template>
                          <span class="amount-unavailable" v-else>{{ $t('deposits.amount_unavailable') }}</span>
                        </div>
                      </div>
                      <div class="estimate-metric estimate-metric-primary">
                        <div class="estimate-metric-label">
                          {{ $t('deposits.deposit_amount') }}
                        </div>
                        <div class="estimate-metric-value">
                          <template v-if="hasAmount(estimatedPayoutAmount)">
                            {{
                              formatter({
                                type: 'currency',
                                data: estimatedPayoutAmount,
                                currencyCode: aggregateCurrency
                              })
                            }}
                            <span class="currency-code-suffix" v-if="showCurrencyTabs">{{ aggregateCurrency }}</span>
                          </template>
                          <ToolTip
                            v-else
                            placement="top-start"
                            :content="amountUnavailableTooltip"
                            :arrow="false"
                          >
                            <span class="amount-unavailable">{{ $t('deposits.amount_unavailable') }}</span>
                          </ToolTip>
                        </div>
                      </div>
                    </div>
                    <div class="estimate-footer">
                      <c-icon name="alert-outline" :size="12" />
                      <div class="estimate-footer-text">
                        <span>
                          {{ $t('deposits.estimated_future_deposits_footer') }}
                        </span>
                      </div>
                    </div>
                  </template>
                  <div class="statistics-container" v-else>
                    <div class="data-container">
                      <div class="box loading-shimmer"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="currency-breakdown-section" v-if="showCurrencyTabs">
          <div class="currency-breakdown-card">
            <div class="currency-breakdown-header">
              <div class="currency-breakdown-heading">
                <div class="card-title">
                  {{ $t('deposits.currency_breakdown') }}
                </div>
                <div class="currency-breakdown-subtitle">
                  {{
                    isAllCurrencies
                      ? $t('deposits.currency_breakdown_all_subtitle', {currency: baseCurrency})
                      : $t('deposits.currency_breakdown_single_subtitle')
                  }}
                </div>
              </div>
              <div class="breakdown-total" v-if="isAllCurrencies && !breakdownLoading && breakdownItems.length > 0">
                <div class="breakdown-total-label">
                  {{ $t('deposits.currency_breakdown_total', {currency: baseCurrency}) }}
                </div>
                <div class="breakdown-total-value fs-mask">
                  <template v-if="hasAmount(breakdownTotalInBaseCurrency)">
                    {{
                      formatter({
                        type: 'currency',
                        data: breakdownTotalInBaseCurrency,
                        currencyCode: baseCurrency
                      })
                    }}
                  </template>
                  <ToolTip
                    v-else
                    placement="top-end"
                    :content="$t('deposits.amount_unavailable_tooltip', {currency: baseCurrency})"
                    :arrow="false"
                  >
                    <span class="amount-unavailable">{{ $t('deposits.amount_unavailable') }}</span>
                  </ToolTip>
                </div>
              </div>
            </div>
            <div class="breakdown-loading" v-if="breakdownLoading">
              <div class="box loading-shimmer"></div>
            </div>
            <div class="breakdown-empty" v-else-if="breakdownFailed">
              {{ $t('deposits.error.breakdown', {error: ''}) }}
            </div>
            <div class="breakdown-empty" v-else-if="breakdownItems.length === 0">
              {{ $t('deposits.currency_breakdown_empty') }}
            </div>
            <div class="breakdown-grid" v-else>
              <div
                class="breakdown-item"
                v-for="item in breakdownItems"
                :key="item.currency"
              >
                <div class="breakdown-item-currency">
                  {{ item.currency }}
                </div>
                <div class="breakdown-item-amount fs-mask">
                  {{ formatter({type: 'currency', data: item.amount, currencyCode: item.currency}) }}
                </div>
                <div class="breakdown-item-base fs-mask" v-if="isAllCurrencies && item.currency !== baseCurrency">
                  <span v-if="item.amount_in_base_currency !== undefined && item.amount_in_base_currency !== null">
                    {{ $t('deposits.approximate_prefix') }}{{
                      formatter({type: 'currency', data: item.amount_in_base_currency, currencyCode: baseCurrency})
                    }}
                  </span>
                  <span class="rate-unavailable" v-else>
                    {{ $t('deposits.currency_breakdown_rate_unavailable') }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="section-header">
          {{ $t('deposits.activity') }}
        </div>

        <TableFilterOption
          :disabled="loading.all"
          table-label="Deposits"
          v-bind="tableFilterOptionConfig"
          @childApplyFilter="applyFilter"
          @childClearFilter="applyFilter"
          :date-filter-duration="12"
          :enable-filters="['dateFilter', 'statusFilter', 'typeFilter', 'depositAmountFilter']"
          ref="tableFilterOptionComponent"
        />

        <Search
          placeholder="Search payouts by ID"
          @search="searchDeposits"
          :disabled="deposits === null || deposits.length === 0"
          class="search-container"
        />

        <div class="header-container">
          <div class="settlement-header-count">
            <div v-if="count!==0" class="showing-count-top" :class="{'hide-loader': loading.all || deposits.length===0}">
              {{ $t('deposits.showing') }} <b class="pagination"> {{ depositsCount }} </b>
            </div>
            <div v-else class="showing-count-top" :class="{'hide-loader': loading.all}">
              {{ $t('deposits.noDepositsFound') }}
            </div>
            <div class="loader" :class="{'hide-loader': !loading.all}"></div>
          </div>
          <export-button
            :disabled="loading.all || deposits.length === 0 || searchQuery !== ''"
            :loading="loading.export"
            :id="getElementId('export')"
            :create-export="createExport"
            :tooltip-enabled="!(searchQuery ==='' && deposits.length>0)"
            :tooltip-content="tooltipContent()"
          />
        </div>

        <TableView
          :data="depositsData"
          :loading="loading.all"
          :all-items-loaded="allItemsLoaded"
          :infinite-scroll="searchQuery === ''"
          class="deposits-table"
          entity="deposits"
          :columns="getColumns"
          :column-right-align="[
            'total_amount',
            'deductions',
            'deposit_amount',
          ]"
          :tooltips="[
            'date_of_transactions',
            'deductions'
          ]"
          @clearFilter="clearFilterFromEmptyPlaceholder"
          :clickable-rows="true"
          :fixed-first-column="false"
          @row-clicked="rowClicked"
        >
          <template #deposit="prop">
            <div class="deposit-id-column">
              <div class="status-tag-container">
                <ToolTip
                  v-if="isFailedDeposit(prop.item)"
                  placement="top"
                  :content="getFailureReason(prop.item)"
                  :dismiss-on-click="true"
                >
                  <div
                    class="status"
                    :class="{
                      'failure-status': prop.item.status === 'FAILED',
                      'canceled-status': prop.item.status === 'CANCELED',
                    }"
                  >
                    {{ $t('deposits.status.' + prop.item.status) }}
                  </div>
                </ToolTip>
                <div
                  v-else
                  class="status"
                  :class="{
                    'progress-status': prop.item.status === 'PENDING',
                    'success-status': prop.item.status === 'SUCCEEDED',
                    'na-status': prop.item.status === undefined,
                  }"
                >
                  {{
                    prop.item.status
                      ? $t('deposits.status.' + prop.item.status)
                      : $t('deposits.status.NOT_AVAILABLE')
                  }}
                </div>
              </div>

              <div class="date-and-txid">
                <div class="date_box">
                  <ToolTip
                    placement="top"
                    :content="formatter({type: 'timeWithZone', data: prop.item.created_at})"
                    :dismiss-on-click="true"
                  >
                    {{ formatter({type: 'zonedDate', data: prop.item.created_at}) }}
                  </ToolTip>
                </div>
                <div class="txid">
                  {{ prop.item.id }}
                </div>
              </div>
            </div>
          </template>
          <template #date_of_transactions="prop">
            <ToolTip
              v-if="formatTransactionsTimelineForASettlement(getTransactionTimelineForDeposit(prop.item), 'timeWithZone')"
              placement="top"
              :content="formatTransactionsTimelineForASettlement(getTransactionTimelineForDeposit(prop.item), 'timeWithZone')"
              :dismiss-on-click="true"
              :no-wrap="true"
            >
              {{ formatTransactionsTimelineForASettlement(getTransactionTimelineForDeposit(prop.item), 'zonedDate') }}
            </ToolTip>
            <div class="empty-placeholder" v-else>
              –
            </div>
          </template>
          <template #type="prop">
            <div v-if="prop.item.amount">
              {{ $t('deposits.type.' + prop.item.type) }}
            </div>
            <div class="empty-placeholder" v-else>
              —
            </div>
          </template>
          <template #total_amount="prop">
            <div class="amount-container fs-mask">
              <div class="amount">
                {{ formatter({type: 'currency', data: getTotalAmountForDeposit(prop.item), currencyCode: prop.item.currency}) }}
              </div>
              <div class="currency_code">
                {{ prop.item.currency }}
              </div>
            </div>
          </template>
          <template #deductions="prop">
            <div class="amount-container fs-mask" v-if="getTotalDeductionsForDeposit(prop.item) !== 0">
              <div class="amount">
                {{ formatter({type: 'currency', data: getTotalDeductionsForDeposit(prop.item), currencyCode: prop.item.currency}) }}
              </div>
              <div class="currency_code">
                {{ prop.item.currency }}
              </div>
            </div>
            <div class="empty-placeholder" v-else>
              —
            </div>
          </template>
          <template #deposit_amount="prop">
            <div class="amount-container fs-mask">
              <div class="amount" v-if="getMerchantDepositAmount(prop.item)">
                {{
                  formatter({
                    type: 'currency',
                    data: getMerchantDepositAmount(prop.item),
                    currencyCode: prop.item.currency,
                  })
                }}
              </div>
              <div class="currency_code" v-if="getMerchantDepositAmount(prop.item)">
                {{ prop.item.currency }}
              </div>
              <div class="empty-placeholder" v-else>
                —
              </div>
            </div>
          </template>
        </TableView>

        <PortalFooter />
      </div>
    </div>
    <ModalPopup :show="showSubmitPopup">
      <div slot="body">
        <ExportsPopUp
          :on-cancel="() => {showSubmitPopup = false}"
        />
      </div>
    </ModalPopup>
    <ModalPopup class="deposit-schedule-modal" :show="showDepositScheduleModal">
      <div slot="body">
        <DepositSchedule
          :on-cancel="() => {showDepositScheduleModal = false}"
        />
      </div>
    </ModalPopup>
  </div>
</template>

<script>

import SidebarNav from "@/components/SidebarNav";
import {mapActions, mapGetters} from "vuex";
import Formatter from "@/app/utils/common/functions/formatter";
import deposits from "@/api/deposits";
import TableView from "@/components/common/TableView";
import TableFilterOption from "@/components/common/TableFilterOption";
import {
  SortBy,
  Sort, ResourceType
} from "@/api/paymentApi";
import Search from "@/components/common/Search";
import {portalCheckMixin} from "@/mixin/portalPageCheck";
import ModalPopup from "@/components/common/ModalPopup";
import ExportsPopUp from "@/components/exports/ExportsPopUp";
import {navigateTo, navigateWithParam} from "@/router";
import SearchBar from "@/components/SearchBar";
import PortalFooter from "@/components/portal/PortalFooter.vue";
import { Country, DateFilters, PendoPrefixes } from "@/app/utils/common/constants";
import DepositSchedule from "@/views/DepositSchedule.vue";
import ExportButton from "@/components/common/ExportButton.vue";
import {exportMixin} from "@/mixin/exportMixin";
import {RouteConstants} from "@/router/routeConstants";
import {
  getDateTimeFromObject,
  getDatePeriodFromFilter
} from "@/app/utils/common/functions/dateUtils";
import {balanceAccountMixin} from "@/mixin/balanceAccountMixin";
import ToolTip from "@/components/common/ToolTip.vue";
import {resolvePayoutFailureMessage} from "@/app/utils/common/functions/deposit-util";

// Pseudo-currency for the tab that aggregates every payout currency into the merchant base currency
const ALL_CURRENCIES = 'ALL';
// Current quarter plus the three preceding ones
const QUARTERS_IN_FILTER = 4;

export default {
  name: 'DepositViewNew',
  components: {
    ToolTip, ExportButton, DepositSchedule, SearchBar, Search, TableView, SidebarNav,
    TableFilterOption, ModalPopup, ExportsPopUp, PortalFooter},
  mixins: [portalCheckMixin, exportMixin, balanceAccountMixin],
  data: () => {
    return {
      showDepositScheduleModal: false,
      settlements: [],
      deposits: [],
      offsetId: null,
      listElm: null,
      pageSize: 15,
      dateFilter: {
        startDate: undefined,
        endDate: undefined,
      },
      allItemsLoaded: false,
      loading: {
        all: false,
        export: false,
      },
      sidebarNavCollapsed: false,
      count: 0,
      searchQuery: "",
      searchResults: [],
      depositStatusFilter: [
        {
          name: "SUCCEEDED",
        },
        {
          name: "FAILED",
        },
        {
          name: "PENDING",
        },
      ],
      typeFilter: [
        {
          name: "CREDIT",
        },
        {
          name: "DEBIT",
        },
      ],
      totalAmountFilter: {
        gt: null,
        gte: null,
        lt: null,
        lte: null,
      },
      depositStatus: [],
      depositType: [],
      depositSubType: [],
      tableFilterOptionConfig: {},
      todayFilter: {
        startDate: undefined,
        endDate: undefined,
      },
      total: 0,
      fundsOnTheWay: 0,
      fundsOnHold: 0,
      fundsInReserve: 0,
      // Multi-currency: 'ALL' aggregates into the merchant base currency, otherwise a payout currency
      selectedCurrency: ALL_CURRENCIES,
      payoutCurrencies: [],
      baseCurrency: null,
      lastSettledAt: null,
      summaryLoading: true,
      breakdown: null,
      breakdownLoading: true,
      // Null keeps the current quarter selected as options roll over
      selectedQuarterKey: null,
      showQuarterDropdown: false,
      tabs: [
        {
          name: "TODAY",
        },
        {
          name: "TOMORROW",
        },
      ],
      currentTab: 'TODAY',
      futureDeposit: {
        totalAmountToday: 0,
        totalFeeToday: 0,
        transactionTimelineToday: {
          first_transaction_date: undefined,
          last_transaction_date: undefined
        },
        totalAmountTomorrow: 0,
        totalFeeTomorrow: 0,
        transactionTimelineTomorrow: {
          first_transaction_date: undefined,
          last_transaction_date: undefined
        }
      },
      currentFutureDeposit: {
        totalAmount: 0,
        totalFee: 0,
        transactionTimeline: {
          first_transaction_date: undefined,
          last_transaction_date: undefined
        }
      },
      summaryFailed: false,
      breakdownFailed: false,
      // Only the newest request for each section may write to state, so a slow earlier response
      // cannot overwrite a newer currency or quarter selection.
      summaryRequestId: 0,
      breakdownRequestId: 0,
      onTheWayDepositLoading: true,
      fundsOnHoldDepositLoading: true,
      futureEstimateDepositLoading: true,
      showSubmitPopup: false,
      windowWidth: window.innerWidth,
    }
  },
  computed: {
    Country() {
      return Country
    },
    ALL_CURRENCIES() {
      return ALL_CURRENCIES
    },
    ...mapGetters('app', ['getMerchantId', 'isAdminMode', 'getActivePayfac', 'getMerchantCurrency']),
    ...mapGetters('merchantBalanceReserve', [
      'getCurrentAmountInRefundReserve',
      'getCurrentAmountInChargebackReserve',
      'getPendingAmountToRefundReserve',
      'getPendingAmountToChargebackReserve'
    ]),
    getColumns() {
      let columns = {
        'deposit': 'deposits.columns.deposit',
        'date_of_transactions': 'deposits.columns.date_of_transactions',
        'total_amount': 'deposits.columns.total_amount',
        'deductions': 'deposits.columns.deductions',
        'deposit_amount': 'deposits.columns.deposit_amount',
      }
      if (this.getActivePayfac === "ADYEN") {
        columns.deductions = "deposits.columns.deductions.adyen"
      }
      return columns;
    },
    depositsCount() {
      return this.deposits.length + " / " + this.count;
    },
    depositsData() {
      return this.searchQuery === "" ? this.deposits : this.searchResults;
    },
    // Current quarter first, then the three preceding ones
    quarterOptions() {
      return Array.from({length: QUARTERS_IN_FILTER}, (unused, quartersAgo) => {
        const start = getDateTimeFromObject().minus({quarters: quartersAgo}).startOf('quarter');
        const end = start.endOf('quarter');
        return {
          key: `Q${start.quarter}-${start.year}`,
          range: `${start.toFormat('LLL')} – ${end.toFormat('LLL')} '${end.toFormat('yy')}`,
          startDate: start.toUTC().toJSDate(),
          endDate: end.toUTC().toJSDate(),
        };
      });
    },
    selectedQuarter() {
      return this.quarterOptions.find(quarter => quarter.key === this.selectedQuarterKey)
        ?? this.quarterOptions[0];
    },
    getChargebackReserveInfo() {
      let chargebackReserveInfo = "Chargeback Reserve = "
        + Formatter.format({ type: 'currency', data: this.getCurrentAmountInChargebackReserve, currencyCode: this.getMerchantCurrency});
      if (this.getPendingAmountToChargebackReserve !== 0  && this.getPendingAmountToChargebackReserve !== null) {
        chargebackReserveInfo += " + "
          + Formatter.format({ type: 'currency', data: this.getPendingAmountToChargebackReserve, currencyCode: this.getMerchantCurrency})
          + " (Pending)"
      }
      return chargebackReserveInfo;
    },
    getRefundReserveInfo() {
      let refundReserveInfo =  "Refund Reserve = "
        + Formatter.format({ type: 'currency', data: this.getCurrentAmountInRefundReserve, currencyCode: this.getMerchantCurrency})
      if (this.getPendingAmountToRefundReserve !== 0  && this.getPendingAmountToRefundReserve !== null) {
        refundReserveInfo += " + "
          + Formatter.format({ type: 'currency', data: this.getPendingAmountToRefundReserve, currencyCode: this.getMerchantCurrency})
          + " (Pending)"
      }
      return refundReserveInfo;
    },
    /**
     * A merchant is single-currency only when they are paid out in exactly one currency and that
     * currency is their base currency — All and that currency would show identical numbers, so the
     * tabs, the base-currency conversions and the breakdown section are all redundant.
     */
    showCurrencyTabs() {
      if (this.payoutCurrencies.length === 0) {
        return false;
      }
      return this.payoutCurrencies.length > 1 || this.payoutCurrencies[0] !== this.baseCurrency;
    },
    currencyTabs() {
      return [ALL_CURRENCIES, ...this.payoutCurrencies];
    },
    isAllCurrencies() {
      return this.selectedCurrency === ALL_CURRENCIES;
    },
    // Currency that page-level aggregates are expressed in: the base currency on All, else the tab's
    aggregateCurrency() {
      return this.isAllCurrencies ? this.baseCurrency : this.selectedCurrency;
    },
    // Omitted on All so the API returns every currency
    selectedCurrencyFilter() {
      return this.isAllCurrencies ? undefined : this.selectedCurrency;
    },
    breakdownItems() {
      return this.breakdown?.items ?? [];
    },
    breakdownTotalInBaseCurrency() {
      return this.breakdown?.total_amount_in_base_currency ?? null;
    },
    // Net estimate, or null when the API withheld the amounts because a rate was missing
    estimatedPayoutAmount() {
      if (!this.hasAmount(this.currentFutureDeposit.totalAmount)
        || !this.hasAmount(this.currentFutureDeposit.totalFee)) {
        return null;
      }
      return this.currentFutureDeposit.totalAmount - this.currentFutureDeposit.totalFee;
    },
    // Shown wherever an amount is withheld, so a missing rate never reads as a real zero.
    // A failed request and a missing exchange rate both blank the amount for different reasons.
    amountUnavailableTooltip() {
      return this.summaryFailed
        ? this.$t('deposits.error.summary', {error: ''})
        : this.$t('deposits.amount_unavailable_tooltip', {currency: this.aggregateCurrency});
    },
    getErrToastOptions() {
      return {dismissable: true, timeout: 10000};
    },
    getFundsInReserveFooter() {
      return this.showCurrencyTabs
        ? this.$t('deposits.funds_in_reserve_base_currency_footer', {currency: this.getMerchantCurrency})
        : this.$t('deposits.funds_in_reserve_footer');
    }
  },
  mounted() {
    window.addEventListener('resize', () => {
      this.windowWidth = window.innerWidth
    })
    window.cbStorage.setItem('dateFilter', DateFilters.MONTH_TO_DATE);
    this.portalChecks().then(() => {
      this.initialize();
    });
  },
  methods: {
    formatTransactionsTimelineForASettlement: Formatter.formatTransactionsTimelineForASettlement,
    ...mapActions('app', ['updateAppState']),
    ...mapActions('merchantBalanceReserve', ['updateBalanceReserveState']),
    formatter: Formatter.format,
    setHourOfTheDay: Formatter.setHourOfTheDay,
    rowClicked: function (id, data) {
      navigateWithParam(RouteConstants.DEPOSIT_DETAILS, {depositId: id, data}, this.$route.query);
    },
    isFailedDeposit(deposit) {
      return deposit.status === 'FAILED' || deposit.status === 'CANCELED';
    },
    getFailureReason(deposit) {
      return resolvePayoutFailureMessage(this, deposit);
    },
    navigateToAddFunds() {
      navigateTo(RouteConstants.ADD_FUNDS, true, this.$route.query);
    },
    async initialize() {
      ({ startDate: this.dateFilter.startDate, endDate: this.dateFilter.endDate } =
        getDatePeriodFromFilter(DateFilters.MONTH_TO_DATE))

      this.listElm = document.querySelector('#infinite-list');
      this.listElm?.addEventListener('scroll', () => {
        if ((this.listElm.scrollTop + this.listElm.clientHeight + 1 >= this.listElm.scrollHeight) && this.listElm.scrollTop !== 0 && !this.loading.all) {
          this.loadMore();
        }
      });

      this.tableFilterOptionConfig = {
        statusFilterOption: this.depositStatusFilter,
        typeFilterOption: this.typeFilter,
      }

      this.loadMore();
      this.loadPayoutSummary();
      this.loadPayoutBreakdown();
      //Temporarily disabling it till we find an option to show for Adyen.
      // this.getFundsOnTheWay();
      if (this.getActivePayfac === 'ADYEN') {
        await this.getReserveBalance();
        this.fundsInReserve = this.getCurrentAmountInRefundReserve + this.getCurrentAmountInChargebackReserve;
      } else {
        this.getFundsOnHold(); //used fo finix only
      }
    },
    getQuarterPeriod() {
      const quarter = this.selectedQuarter;
      return {startDate: quarter.startDate, endDate: quarter.endDate};
    },
    // Scopes Total Payouts and the currency breakdown; Activity keeps its own period filter
    selectQuarter(quarterKey) {
      this.showQuarterDropdown = false;
      if (this.selectedQuarter.key === quarterKey) {
        return;
      }
      this.selectedQuarterKey = quarterKey;
      this.loadPayoutSummary();
      this.loadPayoutBreakdown();
    },
    /**
     * Drives Total Payouts, Last settled and the estimated future cards. Amounts already arrive in
     * the right currency, so the page never sums mixed currencies client side.
     */
    loadPayoutSummary() {
      this.summaryLoading = true;
      this.fundsOnHoldDepositLoading = true;
      this.summaryFailed = false;
      const period = this.getQuarterPeriod();
      const requestId = ++this.summaryRequestId;
      return deposits.fetchMerchantPayoutSummary(
        this.getMerchantId, period.startDate, period.endDate, this.selectedCurrencyFilter
      ).then(response => {
        if (requestId !== this.summaryRequestId) {
          return;
        }
        // Kept nullable on purpose: the API withholds the total when a currency could not be
        // converted, and defaulting to 0 here would recreate the understated total it prevents.
        this.total = response.total_amount ?? null;
        this.lastSettledAt = response.last_settled_at ?? null;
        this.baseCurrency = response.base_currency ?? this.getMerchantCurrency;
        this.payoutCurrencies = response.payout_currencies ?? [];
        this.setEstimatedFutureFunds(response);
      }).catch(error => {
        if (requestId !== this.summaryRequestId) {
          return;
        }
        this.summaryFailed = true;
        this.total = null;
        this.lastSettledAt = null;
        this.setEstimatedFutureFunds({});
        this.$danger(this.$t('deposits.error.summary', {error: error.source?.detail ?? ''}),
          this.getErrToastOptions);
      }).finally(() => {
        if (requestId !== this.summaryRequestId) {
          return;
        }
        this.summaryLoading = false;
        this.fundsOnHoldDepositLoading = false;
      });
    },
    loadPayoutBreakdown() {
      this.breakdownLoading = true;
      this.breakdownFailed = false;
      const period = this.getQuarterPeriod();
      const requestId = ++this.breakdownRequestId;
      return deposits.fetchMerchantPayoutBreakdown(
        this.getMerchantId, period.startDate, period.endDate, this.selectedCurrencyFilter
      ).then(response => {
        if (requestId !== this.breakdownRequestId) {
          return;
        }
        this.breakdown = response;
      }).catch(error => {
        if (requestId !== this.breakdownRequestId) {
          return;
        }
        this.breakdownFailed = true;
        this.breakdown = null;
        this.$danger(this.$t('deposits.error.breakdown', {error: error.source?.detail ?? ''}),
          this.getErrToastOptions);
      }).finally(() => {
        if (requestId !== this.breakdownRequestId) {
          return;
        }
        this.breakdownLoading = false;
      });
    },
    setEstimatedFutureFunds(summary) {
      const next = summary.estimated_next ?? {};
      const upcoming = summary.estimated_upcoming ?? {};
      this.futureDeposit.totalAmountToday = next.total_amount ?? null;
      this.futureDeposit.totalFeeToday = next.deductions ?? null;
      this.futureDeposit.transactionTimelineToday = next.transactions_timeline ?? {
        first_transaction_date: undefined,
        last_transaction_date: undefined
      };
      this.futureDeposit.totalAmountTomorrow = upcoming.total_amount ?? null;
      this.futureDeposit.totalFeeTomorrow = upcoming.deductions ?? null;
      this.futureDeposit.transactionTimelineTomorrow = upcoming.transactions_timeline ?? {
        first_transaction_date: undefined,
        last_transaction_date: undefined
      };
      this.applyCurrentFutureDeposit(this.currentTab);
    },
    // Refetches every section: the tab scopes the whole page, not just the activity table
    selectCurrency(currency) {
      if (this.selectedCurrency === currency) {
        return;
      }
      this.selectedCurrency = currency;
      this.resetActivity();
      this.loadMore();
      this.loadPayoutSummary();
      this.loadPayoutBreakdown();
    },
    resetActivity() {
      this.deposits = [];
      this.searchResults = [];
      this.searchQuery = "";
      this.offsetId = null;
      this.allItemsLoaded = false;
      this.count = 0;
    },
    getTotalAmountForDeposit(deposit) {
      if (!deposit.settlements || deposit.settlements.length === 0) {
        return 0;
      }
      return deposit.settlements.reduce((total, settlement) => {
        return total + (settlement.total_amount || 0);
      }, 0);
    },

    getTotalDeductionsForDeposit(deposit) {
      if (!deposit.settlements || deposit.settlements.length === 0) {
        return 0;
      }
      return deposit.settlements.reduce((total, settlement) => {
        return total + (settlement.total_fee || 0);
      }, 0);
    },
    getTransactionTimelineForDeposit(deposit) {
      if (!deposit.settlements || deposit.settlements.length === 0) {
        return null;
      }

      // Find the earliest first_transaction_date and latest last_transaction_date
      let firstDate = null;
      let lastDate = null;

      deposit.settlements.forEach(settlement => {
        if (settlement.transactions_timeline) {
          if (!firstDate || settlement.transactions_timeline.first_transaction_date < firstDate) {
            firstDate = settlement.transactions_timeline.first_transaction_date;
          }
          if (!lastDate || settlement.transactions_timeline.last_transaction_date > lastDate) {
            lastDate = settlement.transactions_timeline.last_transaction_date;
          }
        }
      });

      return firstDate && lastDate ? {
        first_transaction_date: firstDate,
        last_transaction_date: lastDate
      } : null;
    },
    getMerchantDepositAmount(deposit) {
      return deposit.type === 'DEBIT' ? deposit.amount*(-1) : deposit.amount;
    },
    getCommonQueryParams() {
      return {
        cursor: this.offsetId,
        size: this.pageSize,
        sort_by: SortBy.ID,
        sort: Sort.DESC,
        start_date: this.dateFilter.startDate,
        end_date: this.dateFilter.endDate,
        amount_gt: this.totalAmountFilter.gt,
        amount_gte: this.totalAmountFilter.gte,
        amount_lt: this.totalAmountFilter.lt,
        amount_lte: this.totalAmountFilter.lte,
        deposit_status: this.depositStatus,
        deposit_type: this.depositType,
        deposit_sub_type: this.depositSubType,
        currency: this.selectedCurrencyFilter ? [this.selectedCurrencyFilter] : undefined,
      }
    },
    loadMore() {
      if (this.allItemsLoaded === true || this.searchQuery !== '') return;

      this.loading.all = true;

      let params = this.getCommonQueryParams();
      deposits.listMerchantDeposits(
        this.getMerchantId,
        params.cursor,
        params.size,
        params.sort_by,
        params.sort,
        params.start_date,
        params.end_date,
        params.amount_gt,
        params.amount_gte,
        params.amount_lt,
        params.amount_lte,
        params.deposit_status,
        params.deposit_type,
        params.deposit_sub_type,
        params.currency).then(response => {
        this.offsetId = response.cursor;
        this.count = response.count;
        this.deposits = this.deposits.concat(response.deposits)

        if (response.deposits.length < this.pageSize) {
          this.allItemsLoaded = true;
        }
        this.loading.all = false;
      });
    },
    applyFilter: function (filter) {
      this.deposits = []
      this.searchResults = [];
      this.offsetId = null
      this.allItemsLoaded = false;
      this.count = 0;
      this.depositStatus = filter.statusFilter;
      this.depositType = filter.typeFilter;
      this.depositSubType = filter.depositSubType;
      this.totalAmountFilter = filter.depositAmountFilter;
      this.dateFilter.startDate = filter.dateFilterValue.startDate;
      this.dateFilter.endDate = filter.dateFilterValue.endDate;
      this.loadMore(true)
      this.listElm.scrollTo(0, 0);
    },
    searchDeposits: function (value) {
      this.searchQuery = value;
      this.searchResults = this.deposits.filter(
        x => x.id.toLowerCase().includes(value.toLowerCase()))
    },
    clearFilterFromEmptyPlaceholder: function () {
      this.$refs.tableFilterOptionComponent.clearFilter();
    },
    switchTab: function (nextTab) {
      if (this.currentTab === nextTab) {
        return
      }
      this.applyCurrentFutureDeposit(nextTab);
      this.currentTab = nextTab
    },
    applyCurrentFutureDeposit: function (tab) {
      switch (tab) {
        case "TODAY":
          this.currentFutureDeposit.totalAmount = this.futureDeposit.totalAmountToday
          this.currentFutureDeposit.totalFee = this.futureDeposit.totalFeeToday
          this.currentFutureDeposit.transactionTimeline = this.futureDeposit.transactionTimelineToday;
          break;
        case "TOMORROW":
          this.currentFutureDeposit.totalAmount = this.futureDeposit.totalAmountTomorrow
          this.currentFutureDeposit.totalFee = this.futureDeposit.totalFeeTomorrow
          this.currentFutureDeposit.transactionTimeline = this.futureDeposit.transactionTimelineTomorrow;
          break;
      }
    },
    getEstimatedTabs: function (tab) {
      if (tab === 'TODAY') {
        return "NEXT";
      }
      return "UPCOMING";
    },
    hasAmount(value) {
      return value !== null && value !== undefined;
    },
    tooltipContent() {
      if (this.searchQuery !== '') {
        return "Clear search to export payouts";
      }
      if (this.deposits.length === 0) {
        return "No payouts to export";
      }
    },
    getCreateExportParams() {
      return {
        resource_type: ResourceType.DEPOSIT,
        deposit_filter: {
          ...this.getCommonQueryParams(),
          cursor: undefined,
          size: undefined,
          requested_from: "Payouts",
        },
      };
    },
    getElementId(id) {
      return PendoPrefixes.CBpay + this.$route.name + '-' + id.toLowerCase();
    }
  },
}

</script>

<style lang="scss">

.main-container {
  background: $neutral_50;
  height: 100%;
  overflow: scroll;

  & .deposit-schedule-modal {
    & .modal-container {
      max-width: 448px;
      max-height: 788px;

      & .footer {
        padding: 0;
      }
    }
  }
}

.settlements-container {
  left: 256px;
  top: 0;
  position: relative;
  width: calc(100% - 256px);
  padding: 20px 30px 30px 30px;
  transition: all 0.2s ease-in;
  justify-content: center;
  display: flex;

  & .settlements-sub-container {
    width: 100%;
    max-width: 1360px;
  }

  & .settlements-page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    padding: 20px 0 8px 0;

    & .settlements-page-header-title {
      font-style: normal;
      font-weight: 600;
      font-size: 20px;
      line-height: 30px;
      color: $neutral_1000;
      text-align: left;
    }

    & .view-deposit-schedule {
      display: flex;
      align-items: center;
      gap: 4px;
      color: $primary_600;
      font-size: 12px;
      font-weight: $weight-bold;
    }

    & .view-deposit-schedule:hover {
      cursor: pointer;
    }

  }

  & .section-header {
    font-style: normal;
    font-weight: $weight_medium;
    font-size: 16px;
    line-height: 30px;
    color: $neutral_600;
    text-align: left;
    padding: 0 0 12px 0;
  }

  // Stands in for an amount the API withheld because an exchange rate was missing. Muted and
  // lighter than a real figure so it never reads as a value.
  & .amount-unavailable {
    font-style: normal;
    font-weight: $weight_normal;
    font-size: 14px;
    line-height: 20px;
    color: $neutral_400;
    border-bottom: 1px dashed $neutral_300;
    cursor: default;
  }

  // Title rendered inside a card, for sections that own their heading
  // (Estimated Future Payouts, Currency-wise Breakdown).
  & .card-title {
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    color: $neutral_1000;
    text-align: left;
  }

  & .search-container{
    width: 320px;
    margin-bottom: 16px;
  }

  & .table-header-description {
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 18px;
    color: $neutral_600;
    text-align: left;
    padding: 0 0 16px 0;
  }

  & .currency-tabs {
    display: flex;
    flex-direction: row;
    gap: 8px;
    padding: 0 0 16px 0;

    & .currency-tab {
      cursor: pointer;
      padding: 6px 16px;
      border-radius: 16px;
      border: 1px solid $neutral_200;
      background: $color_white;
      font-style: normal;
      font-weight: $weight_medium;
      font-size: 12px;
      line-height: 18px;
      color: $neutral_600;

      &:hover {
        background-color: $primary_fill_light;
      }
    }

    & .currency-tab-active {
      background: $primary_600;
      border-color: $primary_600;
      color: $color_white;

      &:hover {
        background: $primary_600;
      }
    }
  }

  & .currency-breakdown-section {
    margin-bottom: 24px;

    & .currency-breakdown-card {
      background: $color_white;
      border-radius: 6px;
      padding: 16px;
      text-align: left;

      & .currency-breakdown-header {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: flex-start;
        gap: 16px;
        padding-bottom: 12px;

        & .currency-breakdown-subtitle {
          font-style: normal;
          font-weight: $weight_normal;
          font-size: 12px;
          line-height: 18px;
          color: $neutral_400;
        }

        & .breakdown-total {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          text-align: right;
          white-space: nowrap;

          & .breakdown-total-label {
            font-style: normal;
            font-weight: $weight_normal;
            font-size: 11px;
            line-height: 16px;
            color: $neutral_400;
          }

          & .breakdown-total-value {
            font-style: normal;
            font-weight: 600;
            font-size: 18px;
            line-height: 26px;
            color: $neutral_800;
          }
        }
      }

      & .breakdown-loading {
        height: 60px;
      }

      & .breakdown-empty {
        font-size: 14px;
        color: $neutral_400;
      }

      & .breakdown-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
        gap: 12px;

        & .breakdown-item {
          display: flex;
          flex-direction: column;
          border: 1px solid $neutral_200;
          border-radius: 6px;
          background: $neutral_50;
          padding: 12px 16px;

          & .breakdown-item-currency {
            font-style: normal;
            font-weight: 600;
            font-size: 12px;
            line-height: 18px;
            color: $neutral_400;
          }

          & .breakdown-item-amount {
            font-style: normal;
            font-weight: 600;
            font-size: 18px;
            line-height: 26px;
            color: $neutral_800;
          }

          & .breakdown-item-base {
            font-style: normal;
            font-weight: $weight_normal;
            font-size: 12px;
            line-height: 18px;
            color: $neutral_400;

            & .rate-unavailable {
              color: $warn_800;
            }
          }
        }
      }

    }
  }

  // Overview (Total Payouts + Funds in Reserve) sits above Estimated Future Payouts,
  // each section spanning the full width.
  & .deposit-overview-data {
    display: flex;
    flex-direction: column;
    margin-bottom: 24px;

    & .sub-data-container {
      display: flex;
      flex-direction: column;
      flex: 1;

      & .sub-data {
        display: flex;
        flex-direction: row;
        flex: 1;

        & .deposit-statistics-container:nth-child(2) {
          margin-left: 16px;
        }

        // Overview row: Total Payouts takes 60% and the reserve card the remaining 40%
        & .total-payouts-card {
          flex: 6;
        }

        & .reserve-card {
          flex: 4;
        }

        & .deposit-statistics-container {
          display: flex;
          flex: 1;
          position: relative;


          & .deposit-data-container {
            background: #FFFFFF;
            border-radius: 6px;
            padding: 16px;
            text-align: left;
            display: flex;
            flex-direction: column;
            height: 100%;
            flex: 1;

            & .card-title {
              margin-bottom: 8px;
            }

            & .nav {
              display: flex;
              flex-direction: row;
              font-size: 14px;
              font-weight: $weight_bold;
              background-color: transparent;
              padding-left: 0;
              border-bottom: 1px solid #D4D4E8;
              margin-bottom: 12px;
              margin-top: 0;

              &:after {
                content: '';
                height: 2px;
                background-color: $color_white;
              }

              & .nav-item {
                display: flex;
                cursor: pointer;
                align-items: start;
                margin-right: 24px;
                font-style: normal;
                font-weight: 500;
                font-size: 14px;
                line-height: 20px;

                @media screen and (min-width: 801px) {
                  padding: 10px 0;
                }

                @media screen and (max-width: 800px) {
                  padding: 8px 0;
                }

                &:hover {
                  color: $primary_600;
                }

                &.disabled:hover {
                  background-color: $color-white;
                  cursor: not-allowed;
                }
              }

              & .nav-item-active {
                color: $primary_600;
                border-bottom: 2px solid $primary_600;

                & svg {
                  margin-right: 10px;
                  color: $primary_600;
                }
              }
            }
            & .date-of-transactions-info-row {
              display: flex;
              flex-direction: row;
              font-style: normal;
              font-weight: $weight_normal;
              font-size: 11px;
              line-height: 16px;
              color: $neutral_400;
            }

            & .estimate-metrics {
              display: flex;
              flex-direction: row;
              gap: 12px;
              margin-top: 12px;

              @media screen and (max-width: 800px) {
                flex-direction: column;
              }

              & .estimate-metric {
                display: flex;
                flex: 1;
                flex-direction: column;
                gap: 4px;
                padding: 12px 16px;
                border: 1px solid $neutral_200;
                border-radius: 6px;
                background: $neutral_50;
                text-align: left;
              }

              & .estimate-metric-primary {
                border-color: $primary_200;
                background: $primary_50;
              }

              & .estimate-metric-label {
                font-style: normal;
                font-weight: $weight_normal;
                font-size: 12px;
                line-height: 18px;
                color: $neutral_400;
              }

              & .estimate-metric-value {
                font-style: normal;
                font-weight: 600;
                font-size: 18px;
                line-height: 26px;
                color: $neutral_800;

                & .currency-code-suffix {
                  font-weight: $weight_normal;
                  font-size: 12px;
                  color: $neutral_400;
                  margin-left: 4px;
                }
              }
            }

            & .estimate-footer {
              font-style: normal;
              font-weight: 400;
              font-size: 12px;
              line-height: 18px;
              color: $neutral_800;
              display: flex;
              align-items: flex-start;
              height: 100%;
              gap: 4px;
              padding-top: 12px;

              & svg {
                vertical-align: text-top;
                width: 20px;
                height: 18px;
              }

              & .estimate-footer-text {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
              }
            }

          }

          & .statistics-footer {
            font-style: normal;
            font-weight: 400;
            font-size: 12px;
            line-height: 16px;
            color: $neutral_600;
            margin-top: 5px;
          }

          & .deposit-main-container {
            display: flex;
            flex-direction: row;


            & .deposit-sub-data-container {
              display: flex;
              flex-direction: column;
              flex: 3;
              min-width: 130px;

              & .title-container {
                display: flex;
                flex-direction: row;
                height: fit-content;
                align-items: center;
                justify-content: space-between;

                & .funds-in-reserve {
                  display: flex;
                  flex-direction: row;
                  height: fit-content;
                  align-items: center;
                }

                & .statistics-title {
                  font-style: normal;
                  font-weight: 600;
                  font-size: 12px;
                  line-height: 18px;
                  color: $neutral_400;
                  margin-right: 5px;

                  & .no-border {
                    border: none !important;
                    padding: 0;
                  }
                }

                & .quarter-select {
                  position: relative;

                  & .quarter-select-trigger {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    cursor: pointer;
                    font-style: normal;
                    font-weight: $weight_medium;
                    font-size: 12px;
                    line-height: 18px;
                    color: $primary_600;
                  }

                  & .quarter-select-overlay {
                    position: fixed;
                    inset: 0;
                    z-index: 10;
                  }

                  & .quarter-select-menu {
                    position: absolute;
                    top: 24px;
                    right: 0;
                    z-index: 11;
                    min-width: 220px;
                    background: $color_white;
                    border: 1px solid $neutral_100;
                    border-radius: 6px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
                    padding: 4px 0;

                    & .quarter-select-option {
                      display: flex;
                      flex-direction: row;
                      justify-content: space-between;
                      gap: 16px;
                      padding: 8px 16px;
                      cursor: pointer;
                      font-size: 12px;
                      line-height: 18px;

                      &:hover {
                        background-color: $primary_fill_light;
                      }

                      & .quarter-select-option-key {
                        font-weight: $weight_medium;
                        color: $neutral_800;
                      }

                      & .quarter-select-option-range {
                        color: $neutral_400;
                      }
                    }

                    & .quarter-select-option-active .quarter-select-option-key {
                      color: $primary_600;
                    }
                  }
                }

                & .add-fund {
                  display: flex;
                  align-items: center;
                  color: $primary_600;
                  font-size: 12px;
                  gap: 4px;
                  font-weight: $weight-bold;
                }

                & .add-fund:hover {
                  cursor: pointer;
                }
              }
            }

            & .deposit-graph-container {
              display: flex;
              width: 100%;
              flex: 10;
              padding-left: 20px;

              & .chart-holder {
                width: 100%;

                & .chart-wrapper {
                  width: 100%;
                  height: 60px;

                  & .echarts {
                    display: flex;
                    flex: 1;
                    min-width: 500px;

                    & :first-child {
                      display: flex;
                      flex: 1;

                      & :first-child {
                        display: flex;
                        flex: 1;
                      }
                    }
                  }
                }
              }
            }

            & .statistics-amount {
              font-style: normal;
              font-weight: 600;
              font-size: 20px;
              line-height: 30px;
              color: $neutral_800;
              margin-top: 8px;

              & .currency-code-suffix {
                font-weight: $weight_normal;
                font-size: 14px;
                color: $neutral_400;
              }
            }

            & .last-settled {
              font-style: normal;
              font-weight: $weight_normal;
              font-size: 12px;
              line-height: 18px;
              color: $neutral_400;
              margin-top: 4px;
            }
          }
        }
      }

      & .sub-data:nth-child(2) {
        margin-bottom: 16px;
      }
    }

    & .sub-data-container:nth-child(2) {
      padding: 16px 0 0 0;
      margin: 0;
      width: 100%;

      & .sub-data {
        margin: 0;
        margin-bottom: 0;

        & .deposit-statistics-container {
          margin-left: 0;
        }
      }
    }
  }

  &.settlements-container-sidebar-collapse {
    left: 86px;
    width: calc(100% - 86px);
  }

  & .action-header {
    background-color: $color_white;
    width: 100%;
    border-radius: 6px 6px 0px 0px;
    height: 80px;
    justify-content: left;
    display: flex;
    padding: 0 20px;
    margin-top: 20px;
    flex-direction: row;

    & .transaction-count {
      font-style: normal;
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      color: $neutral_800;
      align-self: center;
    }
  }

  .header-container {
    background-color: $color_white;
    width: 100%;
    height: fit-content;
    border: 1px solid $neutral_100;
    border-radius: 6px 6px 0px 0px;
    display: flex;
    flex-direction: row;
    justify-content: right;
    padding: 15px 25px 5px 25px;

    & .settlement-header-count {
      font-style: normal;
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      color: $neutral_800;
      width: 100%;
      text-align: left;
      align-self: center;
      padding-bottom: 10px;
      display: flex;

      & .showing-count-top {
        display: inline-block;
        justify-content: left;

        & .pagination {
          font-weight: $weight_bold;
        }
      }

      & .loader {
        display: inline-block;
        width: 30px;
        height: 30px;
        border: 3px solid $neutral_100;
        border-radius: 50%;
        border-top-color: #fff;
        animation: spin 1s ease-in-out infinite;
        justify-content: center;
      }


      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }

      @-webkit-keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }

      .hide-loader {
        display: none;
        visibility: hidden;
      }

    }

    & .c-button {
      width: fit-content;
      height: 32px;
      font-style: normal;
      font-weight: 600;
      font-size: 12px;
      line-height: 18px;
      margin-left: 15px;
      display: flex;
      flex-direction: row;
      padding: 0;

      & img {
        margin-right: 5px;
      }
    }
  }

  & .deposit-id-column {
    display: flex;
    flex-direction: row;

    & .status-tag-container {
      display: flex;
      text-align: left;
      min-width: 70px;
      flex-direction: row;
      justify-content: space-between;
    }

    & .status {
      font-size: 12px;
      padding: 2px 10px;
      border-radius: 10px;
      height: fit-content;
    }

    & .success-status {
      background-color: $success_100;
      color: $success_800;
      font-style: normal;
      font-weight: 500;
      font-size: 12px;
      line-height: 16px;
    }

    & .failure-status {
      background-color: $danger_50;
      color: $danger_800;
      font-style: normal;
      font-weight: 500;
      font-size: 12px;
      line-height: 16px;
    }

    & .canceled-status {
      background-color: $warn_50;
      color: $warn_800;
      font-style: normal;
      font-weight: 500;
      font-size: 12px;
    }

    & .progress-status {
      background-color: $primary_100;
      color: $primary_800;
      font-style: normal;
      font-weight: 500;
      font-size: 12px;
      line-height: 16px;
    }

    & .na-status {
      background-color: $neutral_50;
      color: $neutral_600;
      font-style: normal;
      font-weight: 500;
      font-size: 12px;
      line-height: 16px;
    }

    & .date-and-txid {
      display: flex;
      flex-direction: column;
      justify-content: start;
      align-items: start;
      margin-left: 8px;

      & .date_box {
        font-size: 14px;
        font-weight: $weight-medium;
        line-height: 20px;
        color: $neutral_800;
        display: flex;
        flex-direction: row;

        & .amount {
          margin-right: 5px;
        }

        & .currency_code {
          color: $neutral_400;
          font-size: 14px;
        }
      }

      & .txid {
        font-weight: $weight_normal;
        color: $neutral_400;
        font-size: 12px;
        line-height: 20px;

      }
    }
  }

  & .type-column {
    display: flex;
    min-height: 60px;
    align-items: center;

    & .type {
      display: flex;
      flex-direction: row;
      align-items: start;
      font-size: 14px;
      font-weight: $weight-medium;
      line-height: 20px;
      color: $neutral_800;
      width: 100%;
      justify-content: start;

      & .empty-placeholder {
        color: #D4D4E8;
        padding-left: 20px;
      }

      & .amount-container {
        justify-content: right;
        display: flex;
        flex-direction: row;
        padding-right: 20%;
        padding-left: 20px;
        width: 100%;

        & .empty-placeholder {
          color: #D4D4E8;
          padding-right: 30px;
        }

        & .amount {
          margin-right: 5px;
        }

        & .currency_code {
          color: #666678;
          font-size: 14px;
        }
      }
    }
  }
}
</style>